/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSection from '@ion/dls/src/components/forms/FormSection';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {MessageEndpointFormFields} from './MessageEndpointUtils';

interface KafkaProducerPropertiesProps {
  formState?: FormState;
  isView?: boolean;
  isLoggedinUserAccount?: boolean;
  onFieldChange?: (e: ChangeEvent) => void;
};

/**
 * Displays a Kafka producer properties style account message endpoint form
 * & handles all related form functionality.
 *
 * This is strictly to be used in the context of a @see MessageEndpoint component.
 *
 * ```typescript
 *
 * import KafkaProducerProperties from './KafkaProducerProperties';
 *
 * <KafkaProducerProperties isView={isView}
 *   isLoggedinUserAccount={isLoggedinUserAccount}
 *   formState={formState}
 *   onFieldChange={onFieldChange} />
 *
 * ```
 */
export const KafkaProducerProperties = ({
  formState,
  onFieldChange,
  isView = false,
  isLoggedinUserAccount = false
}: KafkaProducerPropertiesProps) => {
  const {
    securityProtocol,
    acknowledgments,
    serializerValue,
    saslJaasConfig,
    saslMechanism,
    serializerKey,
    clientId
  } = translate('admin-groups:dialog.messageEndpoint.fields', {returnObjects: true});

  const producerFieldsConfig = [
    {
      key    : MessageEndpointFormFields.SECURITY_PROTOCOL,
      config : securityProtocol
    },
    {
      key    : MessageEndpointFormFields.VALUE_SERIALIZER,
      config : serializerValue
    },
    {
      key    : MessageEndpointFormFields.SASL_MECHANISM,
      config : saslMechanism
    },
    {
      key    : MessageEndpointFormFields.ACKNOWLEDGMENTS,
      config : acknowledgments
    },
    {
      key    : MessageEndpointFormFields.SASL_JAAS_CONFIG,
      config : saslJaasConfig
    },
    {
      key    : MessageEndpointFormFields.KEY_SERIALIZER,
      config : serializerKey
    },
    {
      key    : MessageEndpointFormFields.CLIENT_ID,
      config : clientId
    }
  ];

  return (
    <FormSection bottomBorder
      title={translate('admin-groups:dialog.messageEndpoint.producerProperties')}>
      {producerFieldsConfig?.map(({key, config}) => (
        <FormItem key={key}>
          <FormTextField fieldLabel={config?.label}
            placeholder={config?.placeholder}
            value={formState?.[key]?.value}
            name={key}
            onChange={onFieldChange}
            disabled={isView || isLoggedinUserAccount} />
        </FormItem>
      ))}
    </FormSection>
  );
};

export default KafkaProducerProperties;
